import toNumber from 'lodash/toNumber'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import CurrencyFormatter from '@/components/currency-formatter'
import useGetSuiteInfo from '@/hooks/get-suite-info'
import { colors, breakpoints } from '@/styles'
import { getImageUrl } from '@/utils/image'
import { FormattedAddress, getPaymentFrequency } from '@/utils/listings'
import { Label } from '@/components/text'

const OuterWrapper = styled.div<{ noDivider?: boolean }>`
  background: ${colors.white};
  ${props =>
    !props.noDivider
      ? `
    border-bottom: 1px solid ${colors.grey};
  `
      : `
    padding-bottom: 1px;
  `}
`

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: ${colors.white};
  overflow: hidden;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 24px 20px 20px;
  text-align: left;
`

const Image = styled.img`
  margin-right: 20px;
  max-height: 88px;
  min-width: 120px;
  object-fit: cover;
  border-radius: 10px;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    margin-right: 10px;
    height: 60px;
    max-height: 60px;
    min-width: unset;
  }
`

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px 0;
`

const Info = styled.span`
  color: ${colors.regular};
  display: inline-block;

  :not(:last-child) {
    margin-right: 10px;
    padding-right: 10px;
    border-right: 1px solid ${colors.grey48};
  }
`

const LoadingState = styled.div`
  background: ${colors.white};
  border-bottom: 1px solid ${colors.grey};
  width: 100%;
  height: 130px;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    height: 302px;
  }
`

type SuiteInfoProps = {
  listingId: string
  noDivider?: boolean
}

const SuiteInfo: React.FC<SuiteInfoProps> = ({ listingId, noDivider = false }) => {
  const { t } = useTranslation()
  const data = useGetSuiteInfo(listingId)
  if (!data) return <LoadingState />
  const bedrooms = (() => {
    if (data.unit_type_scope_txt_id === 'single')
      return t('b.listing.view.private_room.text', 'Private room')

    const count = toNumber(data.bedrooms)
    if (isNaN(count)) return ''
    if (count === 0) return t('common.studio')
    return t('b.listing.view.count_bedrooms.text', {
      count,
      defaultValue: '{{count}} Bedroom',
      defaultValue_plural: '{{count}} Bedrooms'
    })
  })()

  const bathrooms = (() => {
    const count = toNumber(data.bathrooms)
    if (isNaN(count)) return ''
    return t('b.listing.view.count_bathrooms.text', {
      count,
      defaultValue: '{{count}} Bathroom',
      defaultValue_plural: '{{count}} Bathrooms'
    })
  })()
  const image = getImageUrl(data.cover_photo, { width: 369, height: 264 })
  return (
    <OuterWrapper noDivider={noDivider}>
      <Wrapper>
        {!!image && <Image src={image} alt="" />}
        <ContentWrapper>
          <Label type="large">
            <FormattedAddress listing={data} />
          </Label>
          <Label fontSize="14px">
            {(!!data.price || !!data.price_frequency) && (
              <Info>
                <CurrencyFormatter value={data.price} /> /{' '}
                {getPaymentFrequency(data.price_frequency)?.toLowerCase()}
              </Info>
            )}
            {bedrooms && <Info>{bedrooms}</Info>}
            {data.unit_type_scope_txt_id === 'entire' && bathrooms && <Info>{bathrooms}</Info>}
          </Label>
        </ContentWrapper>
      </Wrapper>
    </OuterWrapper>
  )
}

export default SuiteInfo
